import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

/**
 * Building blocks of the printed documents (direction "A · Munkalap", owner 2026-11-03): the
 * workshop's own logo and data top left, the document's name big top right with its number on an
 * ink label, boxed data, a stamp that sits in the flow (so it never covers text), signatures and
 * the small "made with Garagevo" line. A4, prints well in black and white too.
 * Used by the work order sheets and by the customer's quote page (which has no workshop context,
 * so everything comes in as props).
 */
export function DocHead({ logo, shop, lines, title, number, meta }: {
  logo?: ReactNode;
  shop: string;
  lines: (string | null | undefined | false)[];
  title: string;
  number: string;
  meta?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <header className="ps-head">
      {/* the workshop's own logo top left, when it has one; the name and contacts beside it */}
      <div className="ps-brand">
        {logo}
        <div>
          <div className="ps-shop">{shop}</div>
          <div className="ps-contacts">
            {lines.filter(Boolean).map((l, n) => (
              <div className="ps-contact" key={n}>
                {l}
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="ps-title">
        <div className="ps-kind">{title}</div>
        <div className="ps-number">
          {t("workOrder.print.no")} {number}
        </div>
        {meta && <div className="ps-meta">{meta}</div>}
      </div>
    </header>
  );
}

/** A stamp in the flow of the page: "LEZÁRVA", "ÁTVÉVE", "JÓVÁHAGYVA" with its date and time. */
export function Stamp({ label, when, tone = "green" }: { label: string; when?: string; tone?: "green" | "orange" }) {
  return (
    <div className={`ps-stamp ps-stamp-${tone}`} aria-hidden="true">
      <strong>{label}</strong>
      {when && <small>{when}</small>}
    </div>
  );
}

export function Signatures({ left, right }: { left: string; right: string }) {
  return (
    <div className="ps-signatures">
      <span>{left}</span>
      <span>{right}</span>
    </div>
  );
}

/** The last line of every document: a small grey Garagevo mark. */
export function MadeWith() {
  const { t } = useTranslation();
  return (
    <div className="ps-made">
      <svg width="11" height="11" viewBox="0 0 64 64" aria-hidden="true" focusable="false">
        <path d="M32 4 L56 18 L56 46 L32 60 L8 46 L8 18 Z" fill="#8a97a1" />
        <circle cx="32" cy="32" r="14" fill="#ffffff" />
        <circle cx="32" cy="32" r="7" fill="#8a97a1" />
      </svg>
      {t("workOrder.print.madeWith", { app: t("app.title") })}
    </div>
  );
}

/** Net, VAT and the grand total on an ink band (the label says what the total is). */
export function DocTotals({ rows, label, total }: { rows: { label: string; value: string }[]; label: string; total: string }) {
  return (
    <div className="ps-totals">
      {rows.map((r) => (
        <div key={r.label}>
          <span>{r.label}</span>
          <span>{r.value}</span>
        </div>
      ))}
      <div className="ps-grand">
        <span>{label}</span>
        <span>{total}</span>
      </div>
    </div>
  );
}

/** A label and its value in the boxed data (vehicle, customer). */
export function KeyValues({ rows }: { rows: [string, ReactNode | null | undefined, string?][] }) {
  const shown = rows.filter(([, v]) => v !== null && v !== undefined && v !== "");
  return (
    <dl className="ps-kv">
      {shown.map(([k, v, cls]) => (
        <div key={k}>
          <dt>{k}</dt>
          <dd className={cls}>{v}</dd>
        </div>
      ))}
    </dl>
  );
}
